import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { appointmentAPI, adminAPI, documentAPI, authAPI } from '../../lib/api';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Plus,
  Check,
  X,
  CaretLeft,
  CaretRight,
  UserCircle,
  Stethoscope,
  Upload,
  Image,
  CheckCircle,
  IdentificationCard,
  CreditCard,
  ArrowRight,
  ArrowLeft,
  Trash,
  FileText,
  Shield,
  Warning,
  ClockClockwise,
  Prohibit,
  ChatCircle,
  HourglassHigh,
  Question,
  VideoCamera,
  ClipboardText,
  FirstAid,
  Heart,
  Thermometer,
  Syringe,
  FilePlus,
  CalendarPlus
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Calendar } from '../../components/ui/calendar';
import BookingHelper from '../../components/patient/BookingHelper';

const appointmentTypes = [
  { value: 'annual', label: 'Annual Visit' },
  { value: 'sick_visit', label: 'Sick Visit' },
  { value: '3_month_followup', label: '3 Month Follow-up' },
  { value: '6_month_followup', label: '6 Month Follow-up' },
  { value: '2_week_followup', label: '2 Week Follow-up' },
];

export default function AppointmentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [showBooking, setShowBooking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [availableSlots, setAvailableSlots] = useState([]);
  
  // Multi-step booking - NEW FLOW:
  // 1: Patient Info, 2: Appointment Details, 3: Insurance, 4: ID, 5: Forms/Consents, 6: Confirm
  const [bookingStep, setBookingStep] = useState(1);
  const [existingDocs, setExistingDocs] = useState([]);
  
  // Patient info for step 1
  const [patientInfo, setPatientInfo] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    phone: '',
    email: ''
  });
  const [patientInfoVerified, setPatientInfoVerified] = useState(false);
  
  // Document uploads
  const [insuranceFront, setInsuranceFront] = useState(null);
  const [insuranceBack, setInsuranceBack] = useState(null);
  const [licenseFront, setLicenseFront] = useState(null);
  const [licenseBack, setLicenseBack] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Expiration dates
  const [insuranceExpiry, setInsuranceExpiry] = useState('');
  const [licenseExpiry, setLicenseExpiry] = useState('');
  
  // Consents
  const [consentsCompleted, setConsentsCompleted] = useState(false);
  const [hipaaStatus, setHipaaStatus] = useState(null);
  const [consentTemplates, setConsentTemplates] = useState([]);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [selectedConsent, setSelectedConsent] = useState(null);
  const [consentSignature, setConsentSignature] = useState('');
  const [signingConsent, setSigningConsent] = useState(false);
  const [signedConsentsThisSession, setSignedConsentsThisSession] = useState([]);
  
  // Health Intake for booking (Step 6)
  const [reasonForVisit, setReasonForVisit] = useState('');
  const [symptoms, setSymptoms] = useState('');
  const [symptomDuration, setSymptomDuration] = useState('');
  const [vitals, setVitals] = useState({
    blood_pressure: '',
    heart_rate: '',
    temperature: '',
    weight: '',
    height: ''
  });
  const [labFiles, setLabFiles] = useState([]); // MRI, X-rays, blood results
  const [uploadingLabs, setUploadingLabs] = useState(false);
  
  // Booking success state
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookedAppointment, setBookedAppointment] = useState(null);
  
  // Reschedule/Cancel modals
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Booking helper state
  const [showHelper, setShowHelper] = useState(false);
  
  const [formData, setFormData] = useState({
    appointment_type: 'annual',
    scheduled_time: '',
    notes: '',
  });

  const loadAvailableSlots = useCallback(async () => {
    const baseDate = new Date(selectedDate);
    
    try {
      // Use real API to check availability
      const response = await appointmentAPI.checkAvailability(selectedDate);
      const slotsData = response.data.slots || [];
      
      // Map API response to display format
      const slots = slotsData.map(slot => ({
        datetime: slot.datetime,
        time: slot.time,
        available: slot.available,
        remainingCapacity: slot.remaining_capacity,
        patientAlreadyBooked: slot.patient_already_booked
      }));
      
      setAvailableSlots(slots);
      
      // Show warning if patient already has appointment that day
      if (response.data.patient_has_appointment) {
        toast.warning('You already have an appointment on this day');
      }
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error checking availability:', error);
      // Fallback to showing all slots as available if API fails
      const slots = [];
      for (const hour of [9, 10, 11, 13, 14, 15, 16]) {
        const slotTime = new Date(baseDate);
        slotTime.setHours(hour, 0, 0, 0);
        slots.push({
          datetime: slotTime.toISOString(),
          time: `${hour}:00`,
          available: true,
        });
      }
      setAvailableSlots(slots);
    }
  }, [selectedDate]);

  const loadData = async () => {
    try {
      const [appointmentsRes, providersRes] = await Promise.all([
        appointmentAPI.getAll(),
        adminAPI.getProviders().catch(() => ({ data: [] })),
      ]);
      setAppointments(appointmentsRes.data);
      setProviders(providersRes.data);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExistingDocuments = async () => {
    try {
      const response = await documentAPI.getMyDocs();
      setExistingDocs(response.data || []);
      
      // Check which documents already exist and get their expiration dates
      const docs = response.data || [];
      const insuranceFrontDoc = docs.find(d => d.document_type === 'insurance_front');
      const insuranceBackDoc = docs.find(d => d.document_type === 'insurance_back');
      const licenseFrontDoc = docs.find(d => d.document_type === 'drivers_license_front');
      const licenseBackDoc = docs.find(d => d.document_type === 'drivers_license_back');
      
      // If docs exist, mark them as uploaded and prefill expiration dates
      if (insuranceFrontDoc) {
        setInsuranceFront('existing');
        if (insuranceFrontDoc.expiration_date) {
          setInsuranceExpiry(insuranceFrontDoc.expiration_date.split('T')[0]);
        }
      }
      if (insuranceBackDoc) setInsuranceBack('existing');
      if (licenseFrontDoc) {
        setLicenseFront('existing');
        if (licenseFrontDoc.expiration_date) {
          setLicenseExpiry(licenseFrontDoc.expiration_date.split('T')[0]);
        }
      }
      if (licenseBackDoc) setLicenseBack('existing');
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading documents:', error);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Reset all booking-related states when user changes (different patient logs in)
  useEffect(() => {
    // Reset consent and document states when user changes
    setConsentsCompleted(false);
    setHipaaStatus(null);
    setExistingDocs([]);
    setInsuranceFront(null);
    setInsuranceBack(null);
    setLicenseFront(null);
    setLicenseBack(null);
    setInsuranceExpiry('');
    setLicenseExpiry('');
    setPatientInfoVerified(false);
    setShowBooking(false);
    setBookingStep(1);
  }, [user?.id]); // Only trigger when user ID changes

  // Load documents and HIPAA status when booking modal opens - DO NOT pre-fill patient info (HIPAA compliance)
  useEffect(() => {
    if (showBooking && user) {
      // Form starts blank - patient must enter info fresh each booking
      // This prevents HIPAA violations from showing previous patient data
      loadExistingDocuments();
      loadHipaaStatus();
      loadConsentTemplates();
    }
  }, [showBooking, user]);

  useEffect(() => {
    if (showBooking) {
      loadAvailableSlots();
    }
  }, [selectedDate, showBooking, loadAvailableSlots]);

  const loadHipaaStatus = async () => {
    try {
      const response = await documentAPI.getHipaaConsent();
      setHipaaStatus(response.data);
      // Never auto-check - patient must always confirm consents for each new booking
      setConsentsCompleted(false);
    } catch (error) {
      setConsentsCompleted(false);
    }
  };

  const loadConsentTemplates = async () => {
    try {
      const response = await documentAPI.getHipaaTemplates();
      setConsentTemplates(response.data || []);
    } catch (error) {
      console.error('Error loading consent templates:', error);
    }
  };

  const openConsentForSigning = (consent) => {
    setSelectedConsent(consent);
    setConsentSignature('');
    setShowConsentModal(true);
  };

  const signConsent = async () => {
    if (!consentSignature.trim()) {
      toast.error('Please enter your signature');
      return;
    }
    
    setSigningConsent(true);
    try {
      await documentAPI.signHipaaConsent({
        consent_type: selectedConsent.type,
        signature: consentSignature
      });
      
      // Track signed consents this session
      setSignedConsentsThisSession(prev => [...prev, selectedConsent.type]);
      toast.success(`${selectedConsent.title} signed successfully`);
      setShowConsentModal(false);
      setSelectedConsent(null);
      setConsentSignature('');
      
      // Reload status
      loadHipaaStatus();
    } catch (error) {
      console.error('Error signing consent:', error);
      toast.error('Failed to sign consent');
    } finally {
      setSigningConsent(false);
    }
  };

  const handleFileSelect = async (e, docType, setter, expirySetter = null) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Prevent duplicate uploads while one is in progress
    if (uploading) {
      toast.warning('Please wait, upload in progress...');
      return;
    }
    
    // Validate file
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Please upload an image or PDF file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setUploading(true);
    
    // Use Promise-based FileReader to properly handle async flow
    const readFileAsBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    };
    
    try {
      const base64 = await readFileAsBase64(file);
      
      // Try to auto-extract expiration date for ID documents (only images, not PDFs)
      let extractedExpiry = null;
      if (file.type.startsWith('image/') && (docType.includes('insurance') || docType.includes('license'))) {
        try {
          toast.info('Scanning document for expiration date...');
          const ocrResult = await documentAPI.extractExpiry(base64, docType);
          if (ocrResult.data?.success && ocrResult.data?.expiration_date) {
            extractedExpiry = ocrResult.data.expiration_date;
            toast.success(`Found expiration date: ${new Date(extractedExpiry).toLocaleDateString()}`);
            // Auto-fill the expiration date field
            if (expirySetter) {
              expirySetter(extractedExpiry);
            }
          }
        } catch (ocrError) {
          // OCR is optional - don&apos;t show error, user can enter manually
          process.env.NODE_ENV === "development" && console.log('OCR extraction not available, user can enter manually');
        }
      }
      
      // Get current expiration date (prefer extracted, then manual entry)
      const currentExpiry = docType.includes('insurance') ? insuranceExpiry : licenseExpiry;
      const finalExpiry = extractedExpiry || currentExpiry || null;
      
      // Upload document with expiration date
      await documentAPI.upload({
        document_type: docType,
        file_name: file.name,
        mime_type: file.type,
        file_data: base64,
        expiration_date: finalExpiry
      });
      
      setter(file.name);
      toast.success('Document uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload document. Please try again.');
      process.env.NODE_ENV === "development" && console.error('Document upload error:', error);
    } finally {
      setUploading(false);
      // Clear file input to allow re-selecting the same file
      e.target.value = '';
    }
  };

  const handleBook = async () => {
    // Validate patient info is verified
    if (!patientInfoVerified) {
      toast.error('Please verify your patient information first');
      setBookingStep(1);
      return;
    }
    
    if (!formData.scheduled_time) {
      toast.error('Please select a time slot');
      setBookingStep(2);
      return;
    }
    
    // Ensure appointment is linked to logged-in patient
    if (!user?.id) {
      toast.error('Cannot book appointment - patient not identified. Please log in again.');
      return;
    }

    // Ensure insurance is uploaded with expiration
    if (!insuranceFront || !insuranceBack || !insuranceExpiry) {
      toast.error('Please upload your insurance card with expiration date');
      setBookingStep(3);
      return;
    }
    
    // Ensure ID is uploaded with expiration
    if (!licenseFront || !licenseBack || !licenseExpiry) {
      toast.error('Please upload your ID with expiration date');
      setBookingStep(4);
      return;
    }
    
    // Ensure consents are completed
    if (!consentsCompleted) {
      toast.error('Please complete all required forms and consents');
      setBookingStep(5);
      return;
    }
    
    setBooking(true);
    try {
      // Include patient_id, patient info, and health intake in the booking request
      const bookingData = {
        ...formData,
        patient_id: user.id,
        patient_name: `${patientInfo.first_name} ${patientInfo.last_name}`,
        patient_dob: patientInfo.date_of_birth,
        patient_phone: patientInfo.phone,
        patient_email: patientInfo.email,
        // Health intake data
        reason_for_visit: reasonForVisit,
        symptoms: symptoms || null,
        symptom_duration: symptomDuration || null,
        vitals: (vitals.blood_pressure || vitals.heart_rate || vitals.temperature || vitals.weight || vitals.height) ? vitals : null,
        lab_files_count: labFiles.length
      };
      const response = await appointmentAPI.create(bookingData);
      // Store booked appointment details for confirmation screen
      setBookedAppointment({
        ...bookingData,
        id: response.data?.id,
        scheduled_time: formData.scheduled_time
      });
      setBookingSuccess(true);
      setBookingStep(7); // Go to success confirmation step
      loadData();
    } catch (error) {
      // Handle specific error codes for double-booking
      if (error.response?.status === 409) {
        const errorMessage = error.response?.data?.detail || 'This time slot is no longer available.';
        toast.error(errorMessage);
        loadAvailableSlots();
        setBookingStep(2);
        // Only reset the time, keep the appointment type
        setFormData({ ...formData, scheduled_time: '' });
      } else {
        toast.error('Failed to book appointment. Please try again.');
      }
      process.env.NODE_ENV === "development" && console.error('Booking error:', error);
    } finally {
      setBooking(false);
    }
  };

  const resetBookingForm = () => {
    setFormData({ appointment_type: 'annual', scheduled_time: '', notes: '' });
    setBookingStep(1);
    setInsuranceFront(null);
    setInsuranceBack(null);
    setLicenseFront(null);
    setLicenseBack(null);
    setInsuranceExpiry('');
    setLicenseExpiry('');
    setPatientInfoVerified(false);
    setConsentsCompleted(false);
    setHipaaStatus(null);
    setExistingDocs([]);
    // Reset health intake fields
    setReasonForVisit('');
    setSymptoms('');
    setSymptomDuration('');
    setVitals({ blood_pressure: '', heart_rate: '', temperature: '', weight: '', height: '' });
    setLabFiles([]);
    // Reset consent session tracking
    setSignedConsentsThisSession([]);
    // Clear ALL patient info to avoid HIPAA violations - completely blank form
    setPatientInfo({
      first_name: '',
      last_name: '',
      date_of_birth: '',
      phone: '',
      email: ''
    });
  };

  // New: Open reschedule modal
  const openRescheduleModal = (apt) => {
    setSelectedAppointment(apt);
    setRescheduleDate(new Date(apt.scheduled_time).toISOString().split('T')[0]);
    setRescheduleTime('');
    setRescheduleReason('');
    setShowRescheduleModal(true);
  };

  // New: Open cancel modal
  const openCancelModal = (apt) => {
    setSelectedAppointment(apt);
    setCancelReason('');
    setShowCancelModal(true);
  };

  // New: Submit reschedule request
  const handleSubmitReschedule = async () => {
    if (!rescheduleTime) {
      toast.error('Please select a new time');
      return;
    }
    
    setSubmitting(true);
    try {
      const requestedTime = `${rescheduleDate}T${rescheduleTime}:00`;
      await appointmentAPI.requestReschedule(selectedAppointment.id, {
        requested_time: requestedTime,
        reason: rescheduleReason || null
      });
      toast.success('Reschedule request submitted. Staff will review and approve.');
      setShowRescheduleModal(false);
      setSelectedAppointment(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit reschedule request');
    } finally {
      setSubmitting(false);
    }
  };

  // New: Submit cancel request
  const handleSubmitCancelRequest = async () => {
    setSubmitting(true);
    try {
      await appointmentAPI.requestCancel(selectedAppointment.id, {
        reason: cancelReason || null
      });
      toast.success('Cancellation request submitted. Staff will review and approve.');
      setShowCancelModal(false);
      setSelectedAppointment(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit cancellation request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSavePatientInfo = async () => {
    // Validate all required fields
    if (!patientInfo.first_name || !patientInfo.last_name || !patientInfo.date_of_birth || !patientInfo.phone || !patientInfo.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      // Save patient info to profile
      await authAPI.updateProfile({
        first_name: patientInfo.first_name,
        last_name: patientInfo.last_name,
        date_of_birth: patientInfo.date_of_birth,
        phone: patientInfo.phone
      });
      setPatientInfoVerified(true);
      toast.success('Patient information saved');
      setBookingStep(2); // Move to appointment details
    } catch (error) {
      toast.error('Failed to save patient information');
    }
  };

  // NEW validation for 6-step flow
  const canProceedToStep2 = patientInfoVerified; // Patient info verified
  const canProceedToStep3 = formData.scheduled_time; // Appointment selected
  const canProceedToStep4 = insuranceFront && insuranceBack && insuranceExpiry; // Insurance uploaded
  const canProceedToStep5 = licenseFront && licenseBack && licenseExpiry; // ID uploaded
  const canProceedToStep6 = consentsCompleted; // Consents completed
  const canConfirmBooking = user?.id && canProceedToStep2 && canProceedToStep3 && canProceedToStep4 && canProceedToStep5 && canProceedToStep6;

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'status-pending';
      case 'checked_in': return 'status-in-progress';
      case 'completed': return 'status-completed';
      case 'cancelled': return 'bg-slate-100 text-slate-600';
      default: return 'status-pending';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  // Filter appointments - only show future scheduled appointments in upcoming
  const now = new Date();
  const upcomingAppointments = appointments.filter(a => {
    if (a.status !== 'scheduled') return false;
    const aptTime = new Date(a.scheduled_time);
    return aptTime >= now; // Only future appointments
  });
  const pastAppointments = appointments.filter(a => {
    if (a.status !== 'scheduled') return true; // Completed, cancelled, etc.
    const aptTime = new Date(a.scheduled_time);
    return aptTime < now; // Past scheduled appointments
  });
  
  // Helper function to format time in AM/PM
  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Helper function to format just time slot (e.g., "9:00" -> "9:00 AM")
  const formatSlotTime = (time24) => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Generate Google Calendar URL for appointment
  const generateGoogleCalendarUrl = (appointment) => {
    if (!appointment?.scheduled_time) return null;
    
    const startDate = new Date(appointment.scheduled_time);
    const endDate = new Date(startDate.getTime() + 30 * 60000); // 30 min appointment
    
    // Format dates for Google Calendar (YYYYMMDDTHHmmssZ)
    const formatGoogleDate = (date) => {
      return date.toISOString().replace(/-|:|\.\d{3}/g, '');
    };
    
    const title = encodeURIComponent(`T-MEDCMA: ${appointment.appointment_type?.replace(/_/g, ' ') || 'Medical Appointment'}`);
    const details = encodeURIComponent(
      `Your medical appointment at T-MEDCMA.\n\n` +
      `Type: ${appointment.appointment_type?.replace(/_/g, ' ') || 'Appointment'}\n` +
      `Patient: ${appointment.patient_name || `${patientInfo.first_name} ${patientInfo.last_name}`}\n\n` +
      `Virtual Visit Link: ${window.location.origin}/patient/video-call/${appointment.id}\n\n` +
      `Please log in 15 minutes before your appointment.`
    );
    const location = encodeURIComponent('Virtual Visit - T-MEDCMA');
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}&details=${details}&location=${location}`;
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Appointments
          </h1>
          <p className="text-slate-500 mt-1">Manage your healthcare appointments</p>
        </div>
        {user?.role === 'patient' && (
          <button
            onClick={() => { resetBookingForm(); setShowBooking(true); }}
            className="btn-teal flex items-center gap-2"
            data-testid="book-appointment-btn"
          >
            <Plus size={18} weight="bold" />
            Book Appointment
          </button>
        )}
      </div>

      {/* Multi-Step Booking Modal */}
      {showBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
            {/* Header with Steps */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">Book Appointment</h2>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setShowHelper(true)}
                    className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                    title="Need help?"
                    data-testid="booking-help-btn"
                  >
                    <Question size={20} />
                  </button>
                  <button onClick={() => { setShowBooking(false); resetBookingForm(); }} className="text-slate-400 hover:text-slate-600">
                    <X size={20} />
                  </button>
                </div>
              </div>
              
              {/* Step Indicator - 7 Steps */}
              <div className="flex items-center justify-between overflow-x-auto pb-2 gap-1">
                {[
                  { num: 1, label: 'Patient', icon: UserCircle },
                  { num: 2, label: 'Schedule', icon: CalendarIcon },
                  { num: 3, label: 'Insurance', icon: CreditCard },
                  { num: 4, label: 'ID', icon: IdentificationCard },
                  { num: 5, label: 'Consents', icon: FileText },
                  { num: 6, label: 'Intake', icon: ClipboardText },
                  { num: 7, label: 'Submit', icon: CheckCircle },
                ].map((step, index) => (
                  <div key={step.num} className="flex items-center flex-shrink-0">
                    <div className={`flex items-center justify-center w-7 h-7 rounded-full ${
                      bookingStep >= step.num 
                        ? 'bg-teal-500 text-white' 
                        : 'bg-slate-100 text-slate-400'
                    }`}>
                      <step.icon size={14} />
                    </div>
                    <span className={`ml-1 text-xs font-medium hidden md:inline ${
                      bookingStep >= step.num ? 'text-teal-600' : 'text-slate-400'
                    }`}>
                      {step.label}
                    </span>
                    {index < 6 && (
                      <div className={`w-3 md:w-4 h-0.5 mx-0.5 md:mx-1 ${
                        bookingStep > step.num ? 'bg-teal-500' : 'bg-slate-200'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="p-6">
              {/* Step 1: Patient Information */}
              {bookingStep === 1 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <UserCircle size={48} className="text-teal-500 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-slate-900">Verify Your Information</h3>
                    <p className="text-slate-500 text-sm">Please confirm or update your details to connect to your medical records</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="label-clinical">First Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={patientInfo.first_name}
                        onChange={(e) => setPatientInfo({...patientInfo, first_name: e.target.value})}
                        className="input-clinical"
                        placeholder="Enter first name"
                        data-testid="patient-first-name"
                      />
                    </div>
                    <div>
                      <label className="label-clinical">Last Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={patientInfo.last_name}
                        onChange={(e) => setPatientInfo({...patientInfo, last_name: e.target.value})}
                        className="input-clinical"
                        placeholder="Enter last name"
                        data-testid="patient-last-name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label-clinical">Date of Birth <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      value={patientInfo.date_of_birth}
                      onChange={(e) => setPatientInfo({...patientInfo, date_of_birth: e.target.value})}
                      className="input-clinical"
                      max={new Date().toISOString().split('T')[0]}
                      data-testid="patient-dob"
                    />
                  </div>

                  <div>
                    <label className="label-clinical">Phone Number <span className="text-red-500">*</span></label>
                    <input
                      type="tel"
                      value={patientInfo.phone}
                      onChange={(e) => setPatientInfo({...patientInfo, phone: e.target.value})}
                      className="input-clinical"
                      placeholder="(555) 123-4567"
                      data-testid="patient-phone"
                    />
                  </div>

                  <div>
                    <label className="label-clinical">Email Address <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      value={patientInfo.email}
                      onChange={(e) => setPatientInfo({ ...patientInfo, email: e.target.value })}
                      className="input-clinical"
                      placeholder="Enter your email"
                      data-testid="patient-email"
                    />
                    <p className="text-xs text-slate-500 mt-1">Email is used to connect your appointment</p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Warning size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-amber-800 font-medium">Important</p>
                        <p className="text-sm text-amber-700">This information will be used to connect your appointment to your medical records. Please ensure it matches your ID exactly.</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleSavePatientInfo}
                      disabled={!patientInfo.first_name || !patientInfo.last_name || !patientInfo.date_of_birth || !patientInfo.phone}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium ${
                        patientInfo.first_name && patientInfo.last_name && patientInfo.date_of_birth && patientInfo.phone
                          ? 'btn-teal'
                          : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }`}
                      data-testid="save-patient-info-btn"
                    >
                      Save & Continue
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Appointment Details */}
              {bookingStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <label className="label-clinical">Appointment Type</label>
                    <select
                      value={formData.appointment_type}
                      onChange={(e) => setFormData({ ...formData, appointment_type: e.target.value })}
                      className="input-clinical"
                      data-testid="appointment-type-select"
                    >
                      {appointmentTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="label-clinical mb-3 block">Select Date</label>
                    <div className="bg-white rounded-xl border border-slate-200 p-4 flex justify-center">
                      <Calendar
                        mode="single"
                        selected={selectedDate ? new Date(selectedDate + 'T00:00:00') : undefined}
                        onSelect={(date) => {
                          if (date) {
                            const dateStr = date.toISOString().split('T')[0];
                            setSelectedDate(dateStr);
                          }
                        }}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          // Only disable past dates
                          return date < today;
                        }}
                        className="rounded-md"
                        data-testid="appointment-calendar"
                      />
                    </div>
                    {selectedDate && (
                      <p className="text-sm text-teal-600 mt-2 flex items-center gap-2">
                        <CalendarIcon size={16} />
                        Selected: {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="label-clinical">Available Time Slots</label>
                    <p className="text-xs text-slate-500 mb-3">Click a time slot to select it</p>
                    <div className="grid grid-cols-4 gap-2">
                      {availableSlots.length === 0 ? (
                        <p className="col-span-4 text-sm text-slate-500 py-4 text-center bg-slate-50 rounded-lg">
                          {selectedDate ? 'No slots available for this date' : 'Please select a date first'}
                        </p>
                      ) : (
                        availableSlots.map((slot) => (
                          <button
                            key={slot.datetime}
                            type="button"
                            disabled={!slot.available}
                            onClick={() => setFormData({ ...formData, scheduled_time: slot.datetime })}
                            data-testid={`slot-${slot.time}`}
                            title={
                              slot.patientAlreadyBooked 
                                ? 'You already have an appointment at this time' 
                                : !slot.available 
                                  ? 'This slot is fully booked' 
                                  : `${slot.remainingCapacity || 5} spots available`
                            }
                            className={`p-3 rounded-lg text-sm font-medium transition-all relative ${
                              formData.scheduled_time === slot.datetime
                                ? 'bg-teal-500 text-white ring-2 ring-teal-300 ring-offset-2'
                                : slot.patientAlreadyBooked
                                ? 'bg-amber-50 text-amber-400 cursor-not-allowed border border-amber-200'
                                : slot.available
                                ? 'bg-slate-100 hover:bg-teal-50 hover:border-teal-300 border border-transparent text-slate-700'
                                : 'bg-slate-50 text-slate-300 cursor-not-allowed line-through'
                            }`}
                          >
                            <Clock size={14} className="inline mr-1" />
                            {formatSlotTime(slot.time)}
                            {slot.patientAlreadyBooked && (
                              <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 text-white text-xs rounded-full flex items-center justify-center">!</span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="label-clinical">Notes (Optional)</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="input-clinical min-h-[80px]"
                      placeholder="Reason for visit or any additional information..."
                      data-testid="appointment-notes"
                    />
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={() => setBookingStep(1)}
                      className="btn-outline flex items-center gap-2"
                    >
                      <ArrowLeft size={18} />
                      Back
                    </button>
                    <button
                      onClick={() => setBookingStep(3)}
                      disabled={!canProceedToStep3}
                      className="btn-teal flex items-center gap-2"
                      data-testid="next-step-3"
                    >
                      Next: Insurance Card
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Insurance Card Upload */}
              {bookingStep === 3 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <CreditCard size={48} className="text-teal-500 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-slate-900">Upload Insurance Card</h3>
                    <p className="text-slate-500 text-sm">Please upload both front and back of your insurance card</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Insurance Front */}
                    <div className={`border-2 border-dashed rounded-xl p-6 text-center ${
                      insuranceFront ? 'border-teal-300 bg-teal-50' : 'border-slate-300'
                    }`}>
                      {insuranceFront ? (
                        <div>
                          <CheckCircle size={32} className="text-teal-500 mx-auto mb-2" />
                          <p className="text-sm font-medium text-teal-700">
                            {insuranceFront === 'existing' ? 'Insurance Front Uploaded' : insuranceFront}
                          </p>
                          <label className={`cursor-pointer inline-block mt-2 ${uploading ? 'pointer-events-none opacity-50' : ''}`}>
                            <span className="text-xs bg-teal-600 text-white px-3 py-1 rounded-full hover:bg-teal-700">
                              UPLOAD NEW
                            </span>
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => handleFileSelect(e, 'insurance_front', setInsuranceFront, setInsuranceExpiry)}
                              className="hidden"
                              disabled={uploading}
                              data-testid="insurance-front-replace"
                            />
                          </label>
                        </div>
                      ) : (
                        <label className={`cursor-pointer ${uploading ? 'pointer-events-none opacity-50' : ''}`}>
                          <Upload size={32} className="text-slate-400 mx-auto mb-2" />
                          <p className="text-sm font-medium text-slate-700">Insurance Card (Front)</p>
                          <p className="text-xs text-slate-500 mt-1">{uploading ? 'Uploading...' : 'Click to upload'}</p>
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileSelect(e, 'insurance_front', setInsuranceFront, setInsuranceExpiry)}
                            className="hidden"
                            disabled={uploading}
                            data-testid="insurance-front-upload"
                          />
                        </label>
                      )}
                    </div>

                    {/* Insurance Back */}
                    <div className={`border-2 border-dashed rounded-xl p-6 text-center ${
                      insuranceBack ? 'border-teal-300 bg-teal-50' : 'border-slate-300'
                    }`}>
                      {insuranceBack ? (
                        <div>
                          <CheckCircle size={32} className="text-teal-500 mx-auto mb-2" />
                          <p className="text-sm font-medium text-teal-700">
                            {insuranceBack === 'existing' ? 'Insurance Back Uploaded' : insuranceBack}
                          </p>
                          <label className={`cursor-pointer inline-block mt-2 ${uploading ? 'pointer-events-none opacity-50' : ''}`}>
                            <span className="text-xs bg-teal-600 text-white px-3 py-1 rounded-full hover:bg-teal-700">
                              UPLOAD NEW
                            </span>
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => handleFileSelect(e, 'insurance_back', setInsuranceBack, setInsuranceExpiry)}
                              className="hidden"
                              disabled={uploading}
                              data-testid="insurance-back-replace"
                            />
                          </label>
                        </div>
                      ) : (
                        <label className={`cursor-pointer ${uploading ? 'pointer-events-none opacity-50' : ''}`}>
                          <Upload size={32} className="text-slate-400 mx-auto mb-2" />
                          <p className="text-sm font-medium text-slate-700">Insurance Card (Back)</p>
                          <p className="text-xs text-slate-500 mt-1">{uploading ? 'Uploading...' : 'Click to upload'}</p>
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileSelect(e, 'insurance_back', setInsuranceBack, setInsuranceExpiry)}
                            className="hidden"
                            disabled={uploading}
                            data-testid="insurance-back-upload"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Insurance Expiration Date - Required */}
                  <div className="bg-slate-50 rounded-xl p-4">
                    <label className="label-clinical flex items-center gap-2">
                      <CalendarIcon size={16} className="text-slate-500" />
                      Insurance Card Expiration Date <span className="text-red-500">*</span>
                      {insuranceExpiry && <span className="text-xs text-teal-600 font-normal">(Auto-detected or enter manually)</span>}
                    </label>
                    <input
                      type="date"
                      value={insuranceExpiry}
                      onChange={(e) => setInsuranceExpiry(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="input-clinical mt-2"
                      required
                      data-testid="insurance-expiry-date"
                    />
                    <p className="text-xs text-slate-500 mt-1">Required - We'll scan your document to auto-detect the expiration date</p>
                  </div>

                  {uploading && (
                    <div className="text-center py-2">
                      <div className="spinner mx-auto" />
                      <p className="text-sm text-slate-500 mt-2">Uploading...</p>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <button
                      onClick={() => setBookingStep(2)}
                      className="btn-outline flex items-center gap-2"
                    >
                      <ArrowLeft size={18} />
                      Back
                    </button>
                    <button
                      onClick={() => setBookingStep(4)}
                      disabled={!canProceedToStep4}
                      className="btn-teal flex items-center gap-2"
                      data-testid="next-step-4"
                    >
                      Next: Driver&apos;s License
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: Driver's License Upload */}
              {bookingStep === 4 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <IdentificationCard size={48} className="text-teal-500 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-slate-900">Upload Driver's License</h3>
                    <p className="text-slate-500 text-sm">Please upload both front and back of your driver's license</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* License Front */}
                    <div className={`border-2 border-dashed rounded-xl p-6 text-center ${
                      licenseFront ? 'border-teal-300 bg-teal-50' : 'border-slate-300'
                    }`}>
                      {licenseFront ? (
                        <div>
                          <CheckCircle size={32} className="text-teal-500 mx-auto mb-2" />
                          <p className="text-sm font-medium text-teal-700">
                            {licenseFront === 'existing' ? 'License Front Uploaded' : licenseFront}
                          </p>
                          <label className={`cursor-pointer inline-block mt-2 ${uploading ? 'pointer-events-none opacity-50' : ''}`}>
                            <span className="text-xs bg-teal-600 text-white px-3 py-1 rounded-full hover:bg-teal-700">
                              UPLOAD NEW
                            </span>
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => handleFileSelect(e, 'drivers_license_front', setLicenseFront, setLicenseExpiry)}
                              className="hidden"
                              disabled={uploading}
                              data-testid="license-front-replace"
                            />
                          </label>
                        </div>
                      ) : (
                        <label className={`cursor-pointer ${uploading ? 'pointer-events-none opacity-50' : ''}`}>
                          <Upload size={32} className="text-slate-400 mx-auto mb-2" />
                          <p className="text-sm font-medium text-slate-700">Driver&apos;s License (Front)</p>
                          <p className="text-xs text-slate-500 mt-1">{uploading ? 'Uploading...' : 'Click to upload'}</p>
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileSelect(e, 'drivers_license_front', setLicenseFront, setLicenseExpiry)}
                            className="hidden"
                            disabled={uploading}
                            data-testid="license-front-upload"
                          />
                        </label>
                      )}
                    </div>

                    {/* License Back */}
                    <div className={`border-2 border-dashed rounded-xl p-6 text-center ${
                      licenseBack ? 'border-teal-300 bg-teal-50' : 'border-slate-300'
                    }`}>
                      {licenseBack ? (
                        <div>
                          <CheckCircle size={32} className="text-teal-500 mx-auto mb-2" />
                          <p className="text-sm font-medium text-teal-700">
                            {licenseBack === 'existing' ? 'License Back Uploaded' : licenseBack}
                          </p>
                          <label className={`cursor-pointer inline-block mt-2 ${uploading ? 'pointer-events-none opacity-50' : ''}`}>
                            <span className="text-xs bg-teal-600 text-white px-3 py-1 rounded-full hover:bg-teal-700">
                              UPLOAD NEW
                            </span>
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => handleFileSelect(e, 'drivers_license_back', setLicenseBack, setLicenseExpiry)}
                              className="hidden"
                              disabled={uploading}
                              data-testid="license-back-replace"
                            />
                          </label>
                        </div>
                      ) : (
                        <label className={`cursor-pointer ${uploading ? 'pointer-events-none opacity-50' : ''}`}>
                          <Upload size={32} className="text-slate-400 mx-auto mb-2" />
                          <p className="text-sm font-medium text-slate-700">Driver&apos;s License (Back)</p>
                          <p className="text-xs text-slate-500 mt-1">{uploading ? 'Uploading...' : 'Click to upload'}</p>
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileSelect(e, 'drivers_license_back', setLicenseBack, setLicenseExpiry)}
                            className="hidden"
                            disabled={uploading}
                            data-testid="license-back-upload"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* License Expiration Date - Required */}
                  <div className="bg-slate-50 rounded-xl p-4">
                    <label className="label-clinical flex items-center gap-2">
                      <CalendarIcon size={16} className="text-slate-500" />
                      Driver&apos;s License Expiration Date <span className="text-red-500">*</span>
                      {licenseExpiry && <span className="text-xs text-teal-600 font-normal">(Auto-detected or enter manually)</span>}
                    </label>
                    <input
                      type="date"
                      value={licenseExpiry}
                      onChange={(e) => setLicenseExpiry(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="input-clinical mt-2"
                      required
                      data-testid="license-expiry-date"
                    />
                    <p className="text-xs text-slate-500 mt-1">Required - We&apos;ll scan your document to auto-detect the expiration date</p>
                  </div>

                  {uploading && (
                    <div className="text-center py-2">
                      <div className="spinner mx-auto" />
                      <p className="text-sm text-slate-500 mt-2">Uploading...</p>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <button
                      onClick={() => setBookingStep(3)}
                      className="btn-outline flex items-center gap-2"
                    >
                      <ArrowLeft size={18} />
                      Back
                    </button>
                    <button
                      onClick={() => setBookingStep(5)}
                      disabled={!canProceedToStep5}
                      className="btn-teal flex items-center gap-2"
                      data-testid="next-step-5"
                    >
                      Next: Forms & Consents
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 5: Forms & Consents */}
              {bookingStep === 5 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <FileText size={48} className="text-teal-500 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-slate-900">Required Forms & Consents</h3>
                    <p className="text-slate-500 text-sm">Please sign all required HIPAA consent forms for this appointment</p>
                  </div>

                  <div className="space-y-4">
                    {/* Dynamic consent forms from templates */}
                    {consentTemplates.length > 0 ? (
                      consentTemplates.map((template) => {
                        const isSigned = signedConsentsThisSession.includes(template.type);
                        return (
                          <div 
                            key={template.type} 
                            className={`p-4 rounded-xl border ${isSigned ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {isSigned ? (
                                  <CheckCircle size={24} className="text-green-600" />
                                ) : (
                                  <Shield size={24} className="text-amber-600" />
                                )}
                                <div>
                                  <p className="font-medium text-slate-900">{template.title}</p>
                                  <p className="text-sm text-slate-500">{template.description || 'Required consent form'}</p>
                                </div>
                              </div>
                              {isSigned ? (
                                <span className="text-green-600 text-sm font-medium flex items-center gap-1">
                                  <CheckCircle size={14} /> Signed
                                </span>
                              ) : (
                                <button
                                  onClick={() => openConsentForSigning(template)}
                                  className="btn-teal-sm text-sm"
                                >
                                  Sign Now
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      // Fallback if templates don't load
                      <>
                        <div className="p-4 rounded-xl border bg-amber-50 border-amber-200">
                          <div className="flex items-center gap-3">
                            <Shield size={24} className="text-amber-600" />
                            <p className="text-slate-600">Loading consent forms...</p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Acknowledgment checkbox */}
                  <div className={`p-4 rounded-xl border ${consentsCompleted ? 'bg-green-50 border-green-200' : 'bg-teal-50 border-teal-200'}`}>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={consentsCompleted}
                        onChange={(e) => setConsentsCompleted(e.target.checked)}
                        className="mt-1 w-5 h-5 text-teal-600 border-slate-300 rounded focus:ring-teal-500"
                        data-testid="consent-acknowledgment-checkbox"
                      />
                      <div>
                        <p className="text-sm text-teal-900 font-medium">
                          I confirm I have reviewed and signed all required consent forms
                        </p>
                        <p className="text-sm text-teal-700 mt-1">
                          By checking this box, you acknowledge that you have read, understood, and electronically signed all HIPAA consent forms for this appointment.
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={() => setBookingStep(4)}
                      className="btn-outline flex items-center gap-2"
                    >
                      <ArrowLeft size={18} />
                      Back
                    </button>
                    <button
                      onClick={() => setBookingStep(6)}
                      disabled={!canProceedToStep6}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium ${
                        canProceedToStep6
                          ? 'btn-teal'
                          : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }`}
                      data-testid="next-step-6"
                    >
                      Next: Health Intake
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 6: Health Intake / Labs */}
              {bookingStep === 6 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <ClipboardText size={48} className="text-teal-500 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-slate-900">Health Intake</h3>
                    <p className="text-slate-500 text-sm">Tell us why you want to be seen and upload any relevant medical documents</p>
                  </div>

                  {/* Reason for Visit */}
                  <div>
                    <label className="label-clinical">Reason for Visit <span className="text-red-500">*</span></label>
                    <select
                      value={reasonForVisit}
                      onChange={(e) => setReasonForVisit(e.target.value)}
                      className="input-clinical"
                      data-testid="reason-for-visit"
                    >
                      <option value="">Select reason...</option>
                      <option value="annual_checkup">Annual Checkup / Physical</option>
                      <option value="illness">Illness / Not Feeling Well</option>
                      <option value="follow_up">Follow-up from Previous Visit</option>
                      <option value="chronic_condition">Chronic Condition Management</option>
                      <option value="medication_refill">Medication Refill</option>
                      <option value="lab_review">Lab Results Review</option>
                      <option value="new_symptoms">New Symptoms</option>
                      <option value="preventive_care">Preventive Care / Screening</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Symptoms Description */}
                  <div>
                    <label className="label-clinical">Describe Your Symptoms or Concerns</label>
                    <textarea
                      value={symptoms}
                      onChange={(e) => setSymptoms(e.target.value)}
                      className="input-clinical"
                      rows={3}
                      placeholder="Please describe what you're experiencing, any pain, discomfort, or health concerns..."
                      data-testid="symptoms-description"
                    />
                  </div>

                  {/* Duration */}
                  <div>
                    <label className="label-clinical">How Long Have You Had These Symptoms?</label>
                    <select
                      value={symptomDuration}
                      onChange={(e) => setSymptomDuration(e.target.value)}
                      className="input-clinical"
                      data-testid="symptom-duration"
                    >
                      <option value="">Select duration...</option>
                      <option value="today">Started Today</option>
                      <option value="few_days">A Few Days</option>
                      <option value="week">About a Week</option>
                      <option value="two_weeks">1-2 Weeks</option>
                      <option value="month">About a Month</option>
                      <option value="months">Several Months</option>
                      <option value="ongoing">Ongoing / Chronic</option>
                      <option value="na">Not Applicable</option>
                    </select>
                  </div>

                  {/* Vitals Section */}
                  <div className="border border-slate-200 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Heart size={18} className="text-red-500" />
                      Vitals (Optional - if you have a home monitor)
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-xs text-slate-500">Blood Pressure</label>
                        <input
                          type="text"
                          value={vitals.blood_pressure}
                          onChange={(e) => setVitals({...vitals, blood_pressure: e.target.value})}
                          className="input-clinical text-sm"
                          placeholder="e.g., 120/80"
                          data-testid="vitals-bp"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Heart Rate (BPM)</label>
                        <input
                          type="text"
                          value={vitals.heart_rate}
                          onChange={(e) => setVitals({...vitals, heart_rate: e.target.value})}
                          className="input-clinical text-sm"
                          placeholder="e.g., 72"
                          data-testid="vitals-hr"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Temperature (°F)</label>
                        <input
                          type="text"
                          value={vitals.temperature}
                          onChange={(e) => setVitals({...vitals, temperature: e.target.value})}
                          className="input-clinical text-sm"
                          placeholder="e.g., 98.6"
                          data-testid="vitals-temp"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Weight (lbs)</label>
                        <input
                          type="text"
                          value={vitals.weight}
                          onChange={(e) => setVitals({...vitals, weight: e.target.value})}
                          className="input-clinical text-sm"
                          placeholder="e.g., 165"
                          data-testid="vitals-weight"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Height</label>
                        <input
                          type="text"
                          value={vitals.height}
                          onChange={(e) => setVitals({...vitals, height: e.target.value})}
                          className="input-clinical text-sm"
                          placeholder="e.g., 5ft 10in"
                          data-testid="vitals-height"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Medical Documents Upload */}
                  <div className="border border-slate-200 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <FilePlus size={18} className="text-teal-500" />
                      Upload Medical Documents (Optional)
                    </h4>
                    <p className="text-xs text-slate-500 mb-3">
                      Upload any relevant medical records: MRI results, X-rays, blood test results, previous medical records
                    </p>
                    
                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center">
                      <label className={`cursor-pointer ${uploadingLabs ? 'pointer-events-none opacity-50' : ''}`}>
                        <Upload size={32} className="text-slate-400 mx-auto mb-2" />
                        <p className="text-sm font-medium text-slate-700">Click to Upload Documents</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {uploadingLabs ? 'Uploading...' : 'PDF, Images (max 10MB each)'}
                        </p>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          multiple
                          onChange={async (e) => {
                            const files = Array.from(e.target.files);
                            if (files.length === 0) return;
                            
                            setUploadingLabs(true);
                            const newFiles = [];
                            
                            for (const file of files) {
                              if (file.size > 10 * 1024 * 1024) {
                                toast.error(`${file.name} is too large (max 10MB)`);
                                continue;
                              }
                              newFiles.push({
                                name: file.name,
                                type: file.type,
                                size: file.size
                              });
                              
                              // Upload to backend
                              try {
                                const reader = new FileReader();
                                const base64 = await new Promise((resolve, reject) => {
                                  reader.onload = () => resolve(reader.result.split(',')[1]);
                                  reader.onerror = reject;
                                  reader.readAsDataURL(file);
                                });
                                
                                await documentAPI.upload({
                                  document_type: 'medical_record',
                                  file_name: file.name,
                                  mime_type: file.type,
                                  file_data: base64
                                });
                              } catch (error) {
                                toast.error(`Failed to upload ${file.name}`);
                              }
                            }
                            
                            setLabFiles([...labFiles, ...newFiles]);
                            setUploadingLabs(false);
                            if (newFiles.length > 0) {
                              toast.success(`${newFiles.length} document(s) uploaded`);
                            }
                            e.target.value = '';
                          }}
                          className="hidden"
                          disabled={uploadingLabs}
                          data-testid="lab-files-upload"
                        />
                      </label>
                    </div>
                    
                    {/* Uploaded Files List */}
                    {labFiles.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {labFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-slate-50 rounded-lg p-2">
                            <div className="flex items-center gap-2">
                              <FileText size={16} className="text-teal-500" />
                              <span className="text-sm text-slate-700">{file.name}</span>
                            </div>
                            <button
                              onClick={() => setLabFiles(labFiles.filter((_, i) => i !== index))}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={() => setBookingStep(5)}
                      className="btn-outline flex items-center gap-2"
                    >
                      <ArrowLeft size={18} />
                      Back
                    </button>
                    <button
                      onClick={() => setBookingStep(7)}
                      disabled={!reasonForVisit}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium ${
                        reasonForVisit
                          ? 'btn-teal'
                          : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }`}
                      data-testid="next-step-7"
                    >
                      Next: Review & Submit
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 7: Confirmation */}
              {bookingStep === 7 && !bookingSuccess && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <CheckCircle size={48} className="text-teal-500 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-slate-900">Review & Submit to CMA</h3>
                    <p className="text-slate-500 text-sm">Please review your appointment details before submitting</p>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                    {/* Patient Information */}
                    <div className="pb-4 border-b border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Patient Information</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-slate-500">Name:</span>
                          <span className="ml-2 font-medium text-slate-900">{patientInfo.first_name} {patientInfo.last_name}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">DOB:</span>
                          <span className="ml-2 font-medium text-slate-900">{patientInfo.date_of_birth}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Phone:</span>
                          <span className="ml-2 font-medium text-slate-900">{patientInfo.phone}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Email:</span>
                          <span className="ml-2 font-medium text-slate-900">{patientInfo.email}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Appointment Details */}
                    <div className="pb-4 border-b border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Appointment Details</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Appointment Type</span>
                          <span className="font-medium text-slate-900 capitalize">
                            {formData.appointment_type.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Date & Time</span>
                          <span className="font-medium text-slate-900">
                            {formatTime(formData.scheduled_time)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Health Intake */}
                    <div className="pb-4 border-b border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Health Intake</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Reason for Visit</span>
                          <span className="font-medium text-slate-900 capitalize">
                            {reasonForVisit.replace(/_/g, ' ')}
                          </span>
                        </div>
                        {symptoms && (
                          <div>
                            <span className="text-slate-500">Symptoms:</span>
                            <p className="text-slate-900 mt-1 text-sm">{symptoms}</p>
                          </div>
                        )}
                        {symptomDuration && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">Duration</span>
                            <span className="font-medium text-slate-900 capitalize">
                              {symptomDuration.replace(/_/g, ' ')}
                            </span>
                          </div>
                        )}
                        {(vitals.blood_pressure || vitals.heart_rate || vitals.temperature) && (
                          <div className="flex flex-wrap gap-3 mt-2">
                            {vitals.blood_pressure && (
                              <span className="px-2 py-1 bg-teal-50 text-teal-700 rounded text-xs">
                                BP: {vitals.blood_pressure}
                              </span>
                            )}
                            {vitals.heart_rate && (
                              <span className="px-2 py-1 bg-teal-50 text-teal-700 rounded text-xs">
                                HR: {vitals.heart_rate} bpm
                              </span>
                            )}
                            {vitals.temperature && (
                              <span className="px-2 py-1 bg-teal-50 text-teal-700 rounded text-xs">
                                Temp: {vitals.temperature}°F
                              </span>
                            )}
                          </div>
                        )}
                        {labFiles.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">Documents Uploaded</span>
                            <span className="font-medium text-teal-600">{labFiles.length} file(s)</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Documents & Consents */}
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Insurance Card</span>
                        <span className="font-medium text-teal-600 flex items-center gap-1">
                          <CheckCircle size={16} /> Uploaded
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Driver&apos;s License</span>
                        <span className="font-medium text-teal-600 flex items-center gap-1">
                          <CheckCircle size={16} /> Uploaded
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Required Consents</span>
                        <span className="font-medium text-teal-600 flex items-center gap-1">
                          <CheckCircle size={16} /> All signed
                        </span>
                      </div>
                    </div>
                  </div>

                  {!user?.id && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                      <p className="text-red-700 text-sm">Cannot book appointment - patient not identified. Please log in again.</p>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <button
                      onClick={() => setBookingStep(6)}
                      className="btn-outline flex items-center gap-2"
                    >
                      <ArrowLeft size={18} />
                      Back
                    </button>
                    <button
                      onClick={handleBook}
                      disabled={booking || !canConfirmBooking}
                      className={`flex items-center gap-2 px-8 ${canConfirmBooking ? 'btn-teal' : 'bg-slate-200 text-slate-400 cursor-not-allowed rounded-lg'}`}
                      data-testid="confirm-booking-btn"
                    >
                      {booking ? (
                        <div className="spinner" />
                      ) : (
                        <>
                          <CheckCircle size={18} />
                          Submit to CMA
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 7: Booking Success Confirmation */}
              {bookingStep === 7 && bookingSuccess && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={48} className="text-green-500" weight="fill" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900">Appointment Scheduled!</h3>
                    <p className="text-slate-500 text-sm mt-2">Your appointment has been submitted to our CMA for review</p>
                  </div>

                  <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 space-y-4">
                    <div className="text-center">
                      <p className="text-teal-800 font-semibold text-lg">
                        {bookedAppointment?.appointment_type?.replace(/_/g, ' ')}
                      </p>
                      <p className="text-teal-700 text-2xl font-bold mt-2">
                        {bookedAppointment?.scheduled_time && new Date(bookedAppointment.scheduled_time).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                      <p className="text-teal-600 text-xl">
                        {bookedAppointment?.scheduled_time && new Date(bookedAppointment.scheduled_time).toLocaleTimeString('en-US', { 
                          hour: 'numeric', 
                          minute: '2-digit',
                          hour12: true 
                        })}
                      </p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <VideoCamera size={24} className="text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm text-blue-800 font-semibold">Virtual Appointment Link</p>
                        <p className="text-sm text-blue-700 mt-1">
                          Your video call link is ready! You can join up to 15 minutes before your appointment.
                        </p>
                        
                        {/* Direct Join Link */}
                        <div className="mt-3 p-3 bg-white rounded-lg border border-blue-200">
                          <p className="text-xs text-slate-500 mb-2">Your appointment link:</p>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs bg-slate-100 px-2 py-1 rounded text-slate-700 break-all">
                              {window.location.origin}/patient/video-call/{bookedAppointment?.id}
                            </code>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(`${window.location.origin}/patient/video-call/${bookedAppointment?.id}`);
                                toast.success('Link copied to clipboard!');
                              }}
                              className="text-xs text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap"
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                        
                        <p className="text-xs text-blue-600 mt-3">
                          A confirmation email will also be sent to {patientInfo.email}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Warning size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-amber-800 font-semibold">What&apos;s Next?</p>
                        <ul className="text-sm text-amber-700 mt-1 space-y-1">
                          <li>• Your chart is now ready for CMA review</li>
                          <li>• You&apos;ll receive a confirmation email shortly</li>
                          <li>• Log in on your appointment day to join the virtual visit</li>
                          <li>• If you need to reschedule, visit your Appointments page</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Add to Google Calendar Button */}
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CalendarPlus size={24} className="text-blue-600" />
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Add to Your Calendar</p>
                          <p className="text-xs text-slate-500">Get a reminder before your appointment</p>
                        </div>
                      </div>
                      <a
                        href={generateGoogleCalendarUrl(bookedAppointment)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline text-sm px-4 py-2 flex items-center gap-2 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700"
                        data-testid="add-to-google-calendar-btn"
                      >
                        <CalendarPlus size={16} />
                        Add to Google Calendar
                      </a>
                    </div>
                  </div>

                  <div className="flex justify-center pt-4">
                    <button
                      onClick={() => {
                        setShowBooking(false);
                        resetBookingForm();
                        setBookingSuccess(false);
                        setBookedAppointment(null);
                      }}
                      className="btn-teal px-8 py-3 flex items-center gap-2"
                      data-testid="done-booking-btn"
                    >
                      <CheckCircle size={18} />
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Consent Signing Modal */}
      {showConsentModal && selectedConsent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Shield size={24} className="text-teal-600" />
                  {selectedConsent.title}
                </h2>
                <button 
                  onClick={() => { setShowConsentModal(false); setSelectedConsent(null); }} 
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {/* Consent Content */}
              <div className="bg-slate-50 rounded-xl p-4 mb-6 max-h-[300px] overflow-y-auto text-sm text-slate-700 whitespace-pre-wrap">
                {selectedConsent.text || 'Loading consent content...'}
              </div>
              
              {/* Signature Section */}
              <div className="space-y-4">
                <div>
                  <label className="label-clinical">Electronic Signature <span className="text-red-500">*</span></label>
                  <p className="text-xs text-slate-500 mb-2">Type your full legal name to sign this consent form</p>
                  <input
                    type="text"
                    value={consentSignature}
                    onChange={(e) => setConsentSignature(e.target.value)}
                    placeholder="Enter your full name"
                    className="input-clinical"
                    data-testid="consent-signature-input"
                  />
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    By typing your name above and clicking &quot;Sign Consent&quot;, you are electronically signing this document and agreeing to its terms.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => { setShowConsentModal(false); setSelectedConsent(null); }}
                className="btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={signConsent}
                disabled={!consentSignature.trim() || signingConsent}
                className="btn-teal flex items-center gap-2"
                data-testid="sign-consent-btn"
              >
                {signingConsent ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Signing...
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    Sign Consent
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full" data-testid="reschedule-modal">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <ClockClockwise size={24} className="text-teal-600" />
                  Request Reschedule
                </h2>
                <button onClick={() => setShowRescheduleModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-600">
                  <strong>Current appointment:</strong><br />
                  {formatTime(selectedAppointment.scheduled_time)}
                </p>
              </div>
              
              <div>
                <label className="label-clinical">New Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  className="input-clinical"
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="reschedule-date"
                />
              </div>
              
              <div>
                <label className="label-clinical">New Time <span className="text-red-500">*</span></label>
                <select
                  value={rescheduleTime}
                  onChange={(e) => setRescheduleTime(e.target.value)}
                  className="input-clinical"
                  data-testid="reschedule-time"
                >
                  <option value="">Select a time</option>
                  <option value="09:00">9:00 AM</option>
                  <option value="10:00">10:00 AM</option>
                  <option value="11:00">11:00 AM</option>
                  <option value="13:00">1:00 PM</option>
                  <option value="14:00">2:00 PM</option>
                  <option value="15:00">3:00 PM</option>
                  <option value="16:00">4:00 PM</option>
                </select>
              </div>
              
              <div>
                <label className="label-clinical">Reason (optional)</label>
                <textarea
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                  className="input-clinical"
                  rows={3}
                  placeholder="Why do you need to reschedule?"
                  data-testid="reschedule-reason"
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-700">
                  <strong>Note:</strong> Your reschedule request will be reviewed by staff. Your original appointment remains until approved.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowRescheduleModal(false)}
                  className="flex-1 btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitReschedule}
                  disabled={submitting || !rescheduleTime}
                  className="flex-1 btn-teal flex items-center justify-center gap-2"
                  data-testid="submit-reschedule-btn"
                >
                  {submitting ? <div className="spinner" /> : 'Submit Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Request Modal */}
      {showCancelModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full" data-testid="cancel-modal">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Prohibit size={24} className="text-red-600" />
                  Request Cancellation
                </h2>
                <button onClick={() => setShowCancelModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-600">
                  <strong>Appointment:</strong><br />
                  {selectedAppointment.appointment_type?.replace(/_/g, ' ')} on {formatTime(selectedAppointment.scheduled_time)}
                </p>
              </div>
              
              <div>
                <label className="label-clinical">Reason (optional)</label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="input-clinical"
                  rows={3}
                  placeholder="Why do you need to cancel this appointment?"
                  data-testid="cancel-reason"
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-700">
                  <strong>Note:</strong> Your cancellation request will be reviewed by staff. We may contact you to offer alternative options.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 btn-outline"
                >
                  Go Back
                </button>
                <button
                  onClick={handleSubmitCancelRequest}
                  disabled={submitting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
                  data-testid="submit-cancel-btn"
                >
                  {submitting ? <div className="spinner" /> : 'Submit Cancellation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Appointments */}
      <div className="card-clinical mb-6">
        <div className="p-6 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900">Upcoming Appointments</h2>
        </div>
        
        {upcomingAppointments.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarIcon size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">No upcoming appointments</p>
            {user?.role === 'patient' && (
              <button
                onClick={() => { resetBookingForm(); setShowBooking(true); }}
                className="btn-teal flex items-center gap-2 mx-auto"
                data-testid="request-appointment-empty-btn"
              >
                <Plus size={18} weight="bold" />
                Request Appointment
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {upcomingAppointments.map((apt) => (
              <div key={apt.id} className={`p-4 hover:bg-slate-50 ${apt.pending_reschedule || apt.pending_cancel ? 'bg-amber-50' : ''}`} data-testid={`appointment-${apt.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      apt.pending_cancel ? 'bg-red-100' : apt.pending_reschedule ? 'bg-amber-100' : 'bg-teal-100'
                    }`}>
                      {apt.pending_cancel ? (
                        <Prohibit size={24} className="text-red-600" />
                      ) : apt.pending_reschedule ? (
                        <HourglassHigh size={24} className="text-amber-600" />
                      ) : (
                        <CalendarIcon size={24} className="text-teal-600" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-slate-900 capitalize flex items-center gap-2">
                        {apt.appointment_type.replace(/_/g, ' ')}
                        {apt.pending_reschedule && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                            Reschedule Pending
                          </span>
                        )}
                        {apt.pending_cancel && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                            Cancel Pending
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                        <Clock size={14} />
                        {formatTime(apt.scheduled_time)}
                      </div>
                      {apt.provider_name && (
                        <div className="text-sm text-slate-500 flex items-center gap-2">
                          <Stethoscope size={14} />
                          {apt.provider_name}
                        </div>
                      )}
                      
                      {/* Pending reschedule info */}
                      {apt.pending_reschedule && apt.requested_time && (
                        <div className="mt-2 p-2 bg-amber-100 rounded-lg">
                          <p className="text-sm text-amber-800">
                            <strong>Requested new time:</strong> {formatTime(apt.requested_time)}
                          </p>
                          {apt.reschedule_reason && (
                            <p className="text-sm text-amber-700">Reason: {apt.reschedule_reason}</p>
                          )}
                        </div>
                      )}
                      
                      {/* Pending cancel info */}
                      {apt.pending_cancel && apt.cancel_reason && (
                        <div className="mt-2 p-2 bg-red-100 rounded-lg">
                          <p className="text-sm text-red-700">Reason: {apt.cancel_reason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-2">
                    <span className={`status-badge ${getStatusColor(apt.status)}`}>
                      {apt.status}
                    </span>
                    
                    {/* Action buttons - only show if no pending requests */}
                    {user?.role === 'patient' && apt.status === 'scheduled' && !apt.pending_reschedule && !apt.pending_cancel && (
                      <div className="flex flex-col items-end gap-2">
                        {/* Join Video Call - show for today's appointments */}
                        {(() => {
                          const aptDate = new Date(apt.scheduled_time).toDateString();
                          const today = new Date().toDateString();
                          const aptTime = new Date(apt.scheduled_time);
                          const now = new Date();
                          const timeDiff = (aptTime - now) / (1000 * 60);
                          const canJoin = aptDate === today && timeDiff <= 15 && timeDiff > -60;
                          
                          if (canJoin) {
                            return (
                              <button
                                onClick={() => navigate(`/patient/waiting-room/${apt.id}`)}
                                className="bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium px-3 py-1.5 rounded-lg flex items-center gap-1"
                                data-testid={`join-call-${apt.id}`}
                              >
                                <VideoCamera size={16} />
                                Enter Waiting Room
                              </button>
                            );
                          }
                          return null;
                        })()}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openRescheduleModal(apt)}
                            className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center gap-1"
                            data-testid={`reschedule-apt-${apt.id}`}
                          >
                            <ClockClockwise size={16} />
                            Reschedule
                          </button>
                          <button
                            onClick={() => openCancelModal(apt)}
                            className="text-red-500 hover:text-red-700 text-sm font-medium flex items-center gap-1"
                            data-testid={`cancel-apt-${apt.id}`}
                          >
                            <Prohibit size={16} />
                            Cancel
                          </button>
                          <a
                            href={generateGoogleCalendarUrl(apt)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                            data-testid={`add-calendar-${apt.id}`}
                          >
                            <CalendarPlus size={16} />
                            Add to Calendar
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {/* Waiting for approval message */}
                    {(apt.pending_reschedule || apt.pending_cancel) && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <HourglassHigh size={12} />
                        Awaiting staff approval
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Past Appointments */}
      {pastAppointments.length > 0 && (
        <div className="card-clinical">
          <div className="p-6 border-b border-slate-200">
            <h2 className="font-semibold text-slate-900">Past Appointments</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {pastAppointments.slice(0, 5).map((apt) => (
              <div key={apt.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <CalendarIcon size={20} className="text-slate-400" />
                  </div>
                  <div>
                    <div className="font-medium text-slate-700 capitalize">
                      {apt.appointment_type.replace(/_/g, ' ')}
                    </div>
                    <div className="text-sm text-slate-500">
                      {new Date(apt.scheduled_time).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <span className={`status-badge ${getStatusColor(apt.status)}`}>
                  {apt.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Booking Helper Button (when helper is not open and booking modal is not open) */}
      {user?.role === 'patient' && !showHelper && !showBooking && (
        <button
          onClick={() => setShowHelper(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-teal-600 hover:bg-teal-700 text-white rounded-full shadow-lg flex items-center justify-center z-40 transition-all hover:scale-105"
          data-testid="open-booking-helper"
          title="Need help booking?"
        >
          <Question size={24} weight="bold" />
        </button>
      )}

      {/* Booking Helper Component */}
      <BookingHelper
        isOpen={showHelper}
        onClose={() => setShowHelper(false)}
        currentStep={bookingStep}
        onGoToStep={(step) => {
          if (!showBooking) {
            resetBookingForm();
            setShowBooking(true);
          }
          setBookingStep(step);
        }}
        isBookingOpen={showBooking}
      />
    </div>
  );
}
